/**
 * Förhandlingsordning — the variable points.
 *
 * Everything else in the document is frozen clause text (see `clauses.ts`).
 * These two blocks are the parts a negotiation actually fills in: which
 * properties the order covers, and how long it runs.
 */
import { br, details, nodes, p, section, t } from '@hyranai/regta-document-ir';
import { dateVal, numVal, pickCode, propertyListSlot, selectSlot, textVal, whenOption } from '../common/index.js';
import { FAMILY_BLOCKS, optionByCode } from './family.js';
import { avtalstidRules } from './validation.js';
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source: 'base-templates/Förhandlingsordning (Haymanot-approved)' },
};
/**
 * Punkt "Omfattning och undantag" — 3 in the two-part, 4 in the three-part
 * (numbered by the assembler).
 * The designations/addresses are auto-filled from the linked properties by
 * default; the user may switch either to manual and format it freely.
 */
export const omfattning = {
    ...LEGAL,
    blockKey: 'fo.omfattning',
    version: 'v1',
    title: 'Omfattning och undantag',
    summary: 'Which properties and how many apartments the negotiating order covers at signing.',
    slots: [
        propertyListSlot('property_designations', 'Fastighetsbeteckningar', 'designations', { required: true, statutory: true }),
        propertyListSlot('property_addresses', 'Samtliga adresser på fastigheterna', 'addresses', {
            required: true,
            statutory: true,
            help: 'En adress per rad. Hämtas automatiskt från länkade fastigheter.',
        }),
        { key: 'apartment_count', label: 'Antal lägenheter vid undertecknandet', type: 'number', required: true, integer: true, min: 0 },
    ],
    compile: (input, ctx) => [
        section('fo-omfattning', 'Omfattning och undantag', [
            p([t('Förhandlingsordningen omfattar samtliga bostadslägenheter i huset/husen inom följande fastighet(-er):'), br(), textVal(input, 'property_designations')]),
            p([t('Samtliga adresser på fastigheten/fastigheterna:'), br(), textVal(input, 'property_addresses')]),
            p([t('Antal lägenheter vid undertecknandet: '), numVal(ctx, input, 'apartment_count')]),
            p('Hyresförhandlingslagens bestämmelser om när en lägenhet undandras från en förhandlingsordning är tillämpliga.', {
                variant: 'fine_print',
            }),
        ], { keepTogether: true, variant: 'clause' }),
    ],
};
/** Beta contracts store the ADR-0062 code; the legacy lane stored `value`. */
export const AGREEMENT_TERM_OPTIONS = [
    {
        code: 'tidsbestamd',
        value: 'Till ett visst datum',
        label: 'Till ett visst datum',
        summary: 'Runs until a fixed end date, then auto-renews one year at a time unless terminated three months before expiry.',
    },
    {
        code: 'tillsvidare',
        value: 'Tills vidare',
        label: 'Tills vidare',
        summary: 'Runs indefinitely; terminable in writing to the turn of the year falling at least three months after notice.',
    },
];
const TILLSVIDARE = optionByCode(AGREEMENT_TERM_OPTIONS, 'tillsvidare');
/**
 * Punkt "Avtalstid" — 13 in the two-part, 14 in the three-part. A draft
 * without a term choice keeps rendering the fixed-date wording (the legacy
 * default).
 */
export const avtalstid = {
    ...LEGAL,
    blockKey: 'fo.avtalstid',
    version: 'v1',
    title: 'Avtalstid',
    summary: 'The term of the negotiating order — fixed with yearly renewal, or tills vidare — and how it is terminated.',
    slots: [
        selectSlot('agreement_term', 'Avtalstid', AGREEMENT_TERM_OPTIONS, { default: 'tidsbestamd' }),
        { key: 'agreement_start_date', label: 'Avtalet gäller från och med', type: 'date', required: true, statutory: true },
        {
            key: 'agreement_end_date',
            label: 'Avtalet gäller till och med',
            type: 'date',
            required: true,
            statutory: true,
            visibleWhen: { not: whenOption('agreement_term', TILLSVIDARE) },
        },
    ],
    compile: (input, ctx) => {
        const tillsvidare = pickCode(AGREEMENT_TERM_OPTIONS, input.agreement_term) === TILLSVIDARE.code;
        const start = dateVal(ctx, input, 'agreement_start_date');
        const content = tillsvidare
            ? [
                p([t('Detta avtal gäller från och med '), start, t(' och tills vidare.')]),
                p('Uppsägning av detta avtal ska ske skriftligen tidigast till årsskifte som infaller närmast efter tre månader från uppsägningen kommit motparten till handa. Uppsägningen ska delges motparten.', {
                    variant: 'fine_print',
                }),
            ]
            : [
                p([
                    t('Detta avtal gäller från och med '),
                    start,
                    t(' till '),
                    dateVal(ctx, input, 'agreement_end_date'),
                    t('.'),
                ]),
                p('Har skriftlig uppsägning inte gjorts tre månader före avtalsperiodens utgång är avtalet förlängt på ett år i taget. Uppsägningen ska delges motparten.', {
                    variant: 'fine_print',
                }),
            ];
        return [section('fo-avtalstid', 'Avtalstid', content, { keepTogether: true, variant: 'clause' })];
    },
    // An inverted period is a validation rule (error with override), not a
    // compile diagnostic — the user may keep it deliberately (ADR-0101).
    validate: (input, ctx) => avtalstidRules(input, ctx, AGREEMENT_TERM_OPTIONS),
};
/** Includes the family-shared party panels + signing block (registered once, here). */
export const FO_BLOCKS = [omfattning, avtalstid, ...FAMILY_BLOCKS];
export { nodes, details };
//# sourceMappingURL=blocks.js.map